import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiMail, FiPhone, FiMapPin, FiCopy, FiCheck } from 'react-icons/fi';
import LocationMap from './LocationMap';
import ConfidentialityNote from './ConfidentialityNote';

function ContactPanel({ data, onCTAClick }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  // Get translated content
  const title = t('contact.title', { defaultValue: 'Engage with HADEN' });
  const subtitle = t('contact.subtitle', { defaultValue: 'Connect with our engineering team' });
  const factoryAddressLabel = t('contact.factoryAddress', { defaultValue: 'Factory Address' });
  const fromAirportLabel = t('contact.fromAirport', { defaultValue: 'From Airport' });
  const transportationLabel = t('contact.transportation', { defaultValue: 'Transportation' });
  const transportationText = t('contactLabels.transportationText', { defaultValue: 'HADEN can arrange airport pickup upon request' });
  const emailLabel = t('contact.email', { defaultValue: 'Email' });
  const locationLabel = t('contact.location', { defaultValue: 'Location' });
  
  // Location detail labels
  const distanceLabel = t('contactLabels.distance', { defaultValue: 'Distance' });
  const travelTimeLabel = t('contactLabels.travelTime', { defaultValue: 'Travel Time' });
  const routeLabel = t('contactLabels.route', { defaultValue: 'Route' });
  const nearbyLabel = t('contactLabels.nearby', { defaultValue: 'Nearby' });

  const handleCopyAddress = () => {
    const fullAddress = data?.address || '';
    navigator.clipboard.writeText(fullAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Check if location data is available
  const hasLocationData = data?.coordinates?.lat && data?.coordinates?.lng;
  const hasAddressData = data?.address_line1 || data?.city;
  const hasAirportInfo = data?.airport_info?.name;

  return (
    <div 
      id="contact" 
      className="panel panel-light scroll-mt-20" 
      data-testid="contact-panel"
    >
      <div className="panel-content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 heading-premium">
            {title.includes('HADEN') ? (
              <>
                {title.split('HADEN')[0]}
                <span 
                  style={{ 
                    fontFamily: "'Racing Sans One', cursive",
                    letterSpacing: '0.08em',
                    fontSize: '1.15em'
                  }}
                >
                  HADEN
                </span>
                {title.split('HADEN')[1]}
              </>
            ) : title}
          </h2>
          <p className="text-xl max-w-3xl mx-auto mb-12 subheading-premium">
            {subtitle}
          </p>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16"
        >
          <button
            onClick={() => onCTAClick('Request Quote')}
            className="btn-primary p-6 rounded-lg font-semibold text-lg"
            data-testid="contact-request-quote-btn"
          >
            {t('cta.requestQuote', { defaultValue: 'Request Quote' })}
          </button>
          <button
            onClick={() => onCTAClick('Technical Discussion')}
            className="btn-secondary p-6 rounded-lg font-semibold text-lg"
            data-testid="contact-technical-discussion-btn"
          >
            {t('cta.technicalDiscussion', { defaultValue: 'Technical Discussion' })}
          </button>
          <button
            onClick={() => onCTAClick('Factory Visit')}
            className="btn-secondary p-6 rounded-lg font-semibold text-lg"
            data-testid="contact-factory-visit-btn"
          >
            {t('cta.factoryVisit', { defaultValue: 'Factory Visit' })}
          </button>
          <button
            onClick={() => onCTAClick('Upload RFQ')}
            className="btn-primary p-6 rounded-lg font-semibold text-lg"
            data-testid="contact-upload-rfq-btn"
          >
            {t('cta.uploadRfq', { defaultValue: 'Upload RFQ' })}
          </button>
        </motion.div>

        {/* Map and Location Info - 60/40 Split */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.15 }}
          className="grid lg:grid-cols-5 gap-8 mb-12"
        >
          {/* Map - 60% (3 columns) */}
          <div className="lg:col-span-3">
            {hasLocationData ? (
              <LocationMap coordinates={data.coordinates} className="h-full" />
            ) : (
              <div className="h-full min-h-[400px] bg-gray-100 rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <FiMapPin className="text-4xl mx-auto mb-2" />
                  <p>Loading map...</p>
                </div>
              </div>
            )}
          </div>

          {/* Location Details - 40% (2 columns) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Address Card */}
            <div className="bg-gray-50 p-6 rounded-lg card-premium">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <FiMapPin className="text-2xl mr-3" style={{ color: '#101957' }} />
                  <h3 className="font-bold text-lg text-dark-900">{factoryAddressLabel}</h3>
                </div>
                <button
                  onClick={handleCopyAddress}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Copy address"
                >
                  {copied ? (
                    <FiCheck className="text-green-600" />
                  ) : (
                    <FiCopy className="text-gray-600" />
                  )}
                </button>
              </div>
              {hasAddressData ? (
                <div className="text-gray-700 space-y-1">
                  <p className="font-medium">{data?.address_line1}</p>
                  <p>{data?.address_line2}</p>
                  <p>{data?.city}, {data?.state} {data?.pincode}</p>
                  <p>{data?.country}</p>
                </div>
              ) : (
                <div className="text-gray-500 text-sm">
                  <p>Hyderabad, Telangana, India</p>
                  <p className="text-xs mt-2">Full address loading...</p>
                </div>
              )}
            </div>

            {/* Airport Info Card */}
            <div className="bg-primary-50 p-6 rounded-lg card-premium">
              <div className="flex items-center mb-4">
                <span className="text-2xl mr-3">✈️</span>
                <h3 className="font-bold text-lg" style={{ color: '#101957' }}>{fromAirportLabel}</h3>
              </div>
              {hasAirportInfo ? (
                <div className="space-y-2 text-sm" style={{ color: '#0c4a6e' }}>
                  <p><strong>{data?.airport_info?.name}</strong></p>
                  <p>{distanceLabel}: {data?.airport_info?.distance}</p>
                  <p>{travelTimeLabel}: ~{data?.airport_info?.travel_time}</p>
                  <p>{routeLabel}: {data?.airport_info?.route}</p>
                  {data?.nearby_landmark && (
                    <div className="mt-4 pt-4 border-t border-primary-200">
                      <p className="text-xs">
                        <strong>{nearbyLabel}:</strong> {data?.nearby_landmark?.name}
                        <br />
                        <span className="text-gray-600">({data?.nearby_landmark?.note})</span>
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm" style={{ color: '#0c4a6e' }}>
                  <p><strong>Rajiv Gandhi International Airport (RGIA)</strong></p>
                  <p className="text-xs mt-2">Distance details loading...</p>
                </div>
              )}
            </div>

            {/* Transportation Card */}
            <div className="bg-green-50 p-6 rounded-lg card-premium">
              <div className="flex items-center mb-3">
                <span className="text-2xl mr-3">🚗</span>
                <h3 className="font-bold text-lg text-green-900">{transportationLabel}</h3>
              </div>
              <p className="text-sm text-green-800">
                {data?.pickup_service || transportationText}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Contact Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
          className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <FiMail className="text-2xl text-primary-600" />
            </div>
            <h3 className="font-semibold text-dark-900 mb-2">{emailLabel}</h3>
            <a
              href={`mailto:${data?.email}`}
              className="text-primary-600 hover:text-primary-700 transition-colors"
            >
              {data?.email || 'enquiries@haden.in'}
            </a>
          </div>

          {/* Phone - Only show if phone_visible is true and phone number exists */}
          {data?.phone_visible && data?.phone && (
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <FiPhone className="text-2xl text-primary-600" />
              </div>
              <h3 className="font-semibold text-dark-900 mb-2">{t('contact.phone', { defaultValue: 'Phone' })}</h3>
              <a
                href={`tel:${data?.phone}`}
                className="text-gray-600 hover:text-primary-600 transition-colors"
              >
                {data?.phone}
              </a>
            </div>
          )}

          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <FiMapPin className="text-2xl text-primary-600" />
            </div>
            <h3 className="font-semibold text-dark-900 mb-2">{locationLabel}</h3>
            <p className="text-gray-600">
              {data?.city}, {data?.state}
            </p>
          </div>
        </motion.div>

        {/* Compact Confidentiality Note */}
        <ConfidentialityNote variant="compact" />
      </div>
    </div>
  );
}

export default ContactPanel;
